import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  CardActions,
  Chip,
  Avatar,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Fab,
  Paper,
  Stack,
  Divider,
  Badge,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import LoadingSpinner from "../components/common/LoadingSpinner";
import UserAvatar from "../components/common/UserAvatar";
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  PlayArrow as StartIcon,
  Pause as PauseIcon,
  CheckCircle as CompleteIcon,
  Schedule as PendingIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  EmojiEvents as PointsIcon,
  Assignment as ChoreIcon,
  SportsBasketball as AthleticIcon,
  AttachMoney as FinancialIcon,
  Extension as CustomIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import {
  Activity,
  ActivityCategory,
  ActivityStatus,
  User,
  UserRole,
} from "../types";
import {
  getActivitiesByFamily,
  updateActivityStatus,
} from "../services/activities";
import { getFamilyMembers } from "../services/family";
import ActivityForm from "../components/activities/ActivityForm";

const Activities: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [filteredActivities, setFilteredActivities] = useState<Activity[]>([]);
  const [familyMembers, setFamilyMembers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("ALL");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("ALL");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Check if user is admin (parent, teacher, coach)
  const isAdmin =
    user &&
    [UserRole.PARENT, UserRole.TEACHER, UserRole.COACH].includes(user.role);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (user) {
          setLoading(true);
          const [activitiesData, membersData] = await Promise.all([
            getActivitiesByFamily(user.familyId),
            getFamilyMembers(user.familyId),
          ]);

          setActivities(activitiesData);
          setFamilyMembers(
            membersData.filter(
              (member: any) => member.userId && member.status !== "PENDING"
            )
          );
        }
      } catch (err) {
        console.error("Failed to load data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  useEffect(() => {
    let filtered = [...activities];

    // Role-based filtering
    if (!isAdmin) {
      // Children only see their own activities or unassigned activities
      filtered = filtered.filter(
        (activity) => activity.userId === user?.userId || !activity.userId
      );
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (activity) =>
          activity.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          activity.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (categoryFilter !== "ALL") {
      filtered = filtered.filter(
        (activity) => activity.category === categoryFilter
      );
    }

    // Status filter
    if (statusFilter !== "ALL") {
      filtered = filtered.filter(
        (activity) => activity.status === statusFilter
      );
    }

    // Assignee filter (only for admins)
    if (isAdmin && assigneeFilter !== "ALL") {
      if (assigneeFilter === "UNASSIGNED") {
        filtered = filtered.filter((activity) => !activity.userId);
      } else {
        filtered = filtered.filter(
          (activity) => activity.userId === assigneeFilter
        );
      }
    }

    setFilteredActivities(filtered);
  }, [
    activities,
    searchTerm,
    categoryFilter,
    statusFilter,
    assigneeFilter,
    isAdmin,
    user?.userId,
  ]);

  const handleActivityCreated = (newActivity: Activity) => {
    setActivities([...activities, newActivity]);
    setIsCreateDialogOpen(false);
  };

  const handleStatusUpdate = async (
    activityId: string,
    newStatus: ActivityStatus
  ) => {
    try {
      await updateActivityStatus(activityId, newStatus);
      setActivities(
        activities.map((activity) =>
          activity.activityId === activityId
            ? {
                ...activity,
                status: newStatus,
                updatedAt: new Date().toISOString(),
              }
            : activity
        )
      );
    } catch (err) {
      console.error("Failed to update activity status:", err);
    }
  };

  const getCategoryIcon = (category: ActivityCategory) => {
    switch (category) {
      case ActivityCategory.CHORE:
        return <ChoreIcon />;
      case ActivityCategory.ATHLETIC:
        return <AthleticIcon />;
      case ActivityCategory.FINANCIAL:
        return <FinancialIcon />;
      case ActivityCategory.CUSTOM:
        return <CustomIcon />;
      default:
        return <ChoreIcon />;
    }
  };

  const getCategoryColor = (category: ActivityCategory) => {
    switch (category) {
      case ActivityCategory.CHORE:
        return "#FF6B35";
      case ActivityCategory.ATHLETIC:
        return "#2BB3A3";
      case ActivityCategory.FINANCIAL:
        return "#174B67";
      case ActivityCategory.CUSTOM:
        return "#9C27B0";
      default:
        return "#666";
    }
  };

  const getStatusIcon = (status: ActivityStatus) => {
    switch (status) {
      case ActivityStatus.PENDING:
        return <PendingIcon color="warning" />;
      case ActivityStatus.IN_PROGRESS:
        return <StartIcon color="primary" />;
      case ActivityStatus.COMPLETED:
        return <CompleteIcon color="success" />;
      case ActivityStatus.VERIFIED:
        return (
          <Badge badgeContent="✓" color="success">
            <CompleteIcon color="success" />
          </Badge>
        );
      default:
        return <PendingIcon />;
    }
  };

  const getStatusColor = (status: ActivityStatus) => {
    switch (status) {
      case ActivityStatus.PENDING:
        return "warning";
      case ActivityStatus.IN_PROGRESS:
        return "info";
      case ActivityStatus.COMPLETED:
        return "success";
      case ActivityStatus.VERIFIED:
        return "success";
      default:
        return "default";
    }
  };

  const getMember = (userId: string) => {
    return familyMembers.find((m) => m.userId === userId);
  };

  const getMemberName = (userId: string) => {
    const member = getMember(userId);
    return member ? `${member.firstName} ${member.lastName}` : "Unassigned";
  };

  const getNextStatus = (
    currentStatus: ActivityStatus
  ): ActivityStatus | null => {
    switch (currentStatus) {
      case ActivityStatus.PENDING:
        return ActivityStatus.IN_PROGRESS;
      case ActivityStatus.IN_PROGRESS:
        return ActivityStatus.COMPLETED;
      default:
        return null;
    }
  };

  const getStatusAction = (status: ActivityStatus) => {
    switch (status) {
      case ActivityStatus.PENDING:
        return { label: "Start", icon: <StartIcon /> };
      case ActivityStatus.IN_PROGRESS:
        return { label: "Complete", icon: <CompleteIcon /> };
      default:
        return null;
    }
  };

  const stats = {
    total: filteredActivities.length,
    pending: filteredActivities.filter(
      (a) => a.status === ActivityStatus.PENDING
    ).length,
    inProgress: filteredActivities.filter(
      (a) => a.status === ActivityStatus.IN_PROGRESS
    ).length,
    completed: filteredActivities.filter((a) =>
      [ActivityStatus.COMPLETED, ActivityStatus.VERIFIED].includes(a.status)
    ).length,
    totalPoints: filteredActivities
      .filter((a) =>
        [ActivityStatus.COMPLETED, ActivityStatus.VERIFIED].includes(a.status)
      )
      .reduce((sum, a) => sum + a.points, 0),
  };

  if (loading) {
    return (
      <LoadingSpinner
        variant="detailed"
        size="large"
        message="Loading Activities..."
        subMessage="Gathering family activity data"
      />
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: "none" }}>
      <Grid container spacing={3}>
        {/* Header Section */}
        <Grid item xs={12}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              background: "linear-gradient(135deg, #174B67 0%, #2BB3A3 100%)",
              color: "white",
              borderRadius: 3,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h4" fontWeight="bold">
                Family Activities
              </Typography>
              {isAdmin && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setIsCreateDialogOpen(true)}
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.3)" },
                  }}
                >
                  Create Activity
                </Button>
              )}
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    textAlign: "center",
                    backgroundColor: "rgba(255,255,255,0.95)",
                  }}
                >
                  <Typography variant="h3" color="primary" fontWeight="bold">
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Activities
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    textAlign: "center",
                    backgroundColor: "rgba(255,255,255,0.95)",
                  }}
                >
                  <Typography
                    variant="h3"
                    color="warning.main"
                    fontWeight="bold"
                  >
                    {stats.pending}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    textAlign: "center",
                    backgroundColor: "rgba(255,255,255,0.95)",
                  }}
                >
                  <Typography variant="h3" color="info.main" fontWeight="bold">
                    {stats.inProgress}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    In Progress
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    textAlign: "center",
                    backgroundColor: "rgba(255,255,255,0.95)",
                  }}
                >
                  <Typography
                    variant="h3"
                    color="success.main"
                    fontWeight="bold"
                  >
                    {stats.totalPoints}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Points Earned
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Filters Section */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search activities..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    label="Category"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="ALL">All Categories</MenuItem>
                    <MenuItem value={ActivityCategory.CHORE}>Chore</MenuItem>
                    <MenuItem value={ActivityCategory.ATHLETIC}>
                      Athletic
                    </MenuItem>
                    <MenuItem value={ActivityCategory.FINANCIAL}>
                      Financial
                    </MenuItem>
                    <MenuItem value={ActivityCategory.CUSTOM}>Custom</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="ALL">All Status</MenuItem>
                    <MenuItem value={ActivityStatus.PENDING}>Pending</MenuItem>
                    <MenuItem value={ActivityStatus.IN_PROGRESS}>
                      In Progress
                    </MenuItem>
                    <MenuItem value={ActivityStatus.COMPLETED}>
                      Completed
                    </MenuItem>
                    <MenuItem value={ActivityStatus.VERIFIED}>
                      Verified
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {isAdmin && (
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Assignee</InputLabel>
                    <Select
                      value={assigneeFilter}
                      onChange={(e) => setAssigneeFilter(e.target.value)}
                      label="Assignee"
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="ALL">All Members</MenuItem>
                      <MenuItem value="UNASSIGNED">Unassigned</MenuItem>
                      {familyMembers.map((member) => (
                        <MenuItem key={member.userId} value={member.userId}>
                          {member.firstName} {member.lastName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Activities Grid */}
        <Grid item xs={12}>
          {filteredActivities.length === 0 ? (
            <Paper
              elevation={1}
              sx={{
                p: 6,
                textAlign: "center",
                borderRadius: 3,
                background: "linear-gradient(45deg, #f8f9fa, #e9ecef)",
              }}
            >
              <Typography variant="h5" color="text.secondary" gutterBottom>
                No activities found
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {isAdmin
                  ? "Create your first activity to get started!"
                  : "No activities assigned to you yet."}
              </Typography>
              {isAdmin && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setIsCreateDialogOpen(true)}
                  sx={{ mt: 2 }}
                >
                  Create Activity
                </Button>
              )}
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {filteredActivities.map((activity) => {
                const statusAction = getStatusAction(activity.status);
                const canUpdateStatus =
                  activity.userId === user?.userId || isAdmin;

                return (
                  <Grid item xs={12} sm={6} lg={4} key={activity.activityId}>
                    <Card
                      elevation={2}
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 3,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: 4,
                        },
                        border: `3px solid ${getCategoryColor(
                          activity.category
                        )}20`,
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                        {/* Category Badge */}
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          mb={2}
                        >
                          <Chip
                            icon={getCategoryIcon(activity.category)}
                            label={activity.category}
                            size="small"
                            sx={{
                              backgroundColor: getCategoryColor(
                                activity.category
                              ),
                              color: "white",
                              fontWeight: "bold",
                            }}
                          />
                          <Chip
                            icon={getStatusIcon(activity.status)}
                            label={activity.status.replace("_", " ")}
                            size="small"
                            color={getStatusColor(activity.status) as any}
                            variant="outlined"
                          />
                        </Box>

                        {/* Title and Description */}
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                          {activity.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {activity.description}
                        </Typography>

                        {/* Activity Details */}
                        <Stack spacing={1}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getMember(activity.userId) ? (
                              <>
                                <UserAvatar
                                  user={getMember(activity.userId)!}
                                  size={20}
                                />
                                <Typography variant="body2">
                                  {getMemberName(activity.userId)}
                                </Typography>
                              </>
                            ) : (
                              <>
                                <PersonIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  Unassigned
                                </Typography>
                              </>
                            )}
                          </Box>

                          <Box display="flex" alignItems="center" gap={1}>
                            <PointsIcon fontSize="small" color="action" />
                            <Typography variant="body2" fontWeight="medium">
                              {activity.points} points
                            </Typography>
                          </Box>

                          {activity.dueDate && (
                            <Box display="flex" alignItems="center" gap={1}>
                              <CalendarIcon fontSize="small" color="action" />
                              <Typography variant="body2">
                                Due{" "}
                                {new Date(
                                  activity.dueDate
                                ).toLocaleDateString()}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </CardContent>

                      <CardActions sx={{ p: 2, pt: 0 }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={() =>
                            navigate(`/activities/${activity.activityId}`)
                          }
                          sx={{ borderRadius: 2 }}
                        >
                          View Details
                        </Button>

                        {statusAction && canUpdateStatus && (
                          <Button
                            variant="contained"
                            startIcon={statusAction.icon}
                            onClick={() => {
                              const nextStatus = getNextStatus(activity.status);
                              if (nextStatus) {
                                handleStatusUpdate(
                                  activity.activityId,
                                  nextStatus
                                );
                              }
                            }}
                            sx={{
                              borderRadius: 2,
                              backgroundColor: getCategoryColor(
                                activity.category
                              ),
                              "&:hover": {
                                backgroundColor:
                                  getCategoryColor(activity.category) + "DD",
                              },
                            }}
                          >
                            {statusAction.label}
                          </Button>
                        )}
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Grid>

        {/* Create Activity FAB for mobile */}
        {isAdmin && (
          <Fab
            color="primary"
            aria-label="add"
            onClick={() => setIsCreateDialogOpen(true)}
            sx={{
              position: "fixed",
              bottom: 24,
              right: 24,
              display: { xs: "flex", sm: "none" },
              backgroundColor: "#2BB3A3",
              "&:hover": { backgroundColor: "#174B67" },
            }}
          >
            <AddIcon />
          </Fab>
        )}

        {/* Create Activity Dialog */}
        {isCreateDialogOpen && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1300,
              p: 2,
            }}
            onClick={() => setIsCreateDialogOpen(false)}
          >
            <Paper
              elevation={8}
              sx={{
                maxWidth: 600,
                width: "100%",
                maxHeight: "90vh",
                overflow: "auto",
                p: 3,
                borderRadius: 3,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Create New Activity
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <ActivityForm
                onSubmit={handleActivityCreated}
                onCancel={() => setIsCreateDialogOpen(false)}
              />
            </Paper>
          </Box>
        )}
      </Grid>
    </Box>
  );
};

export default Activities;
